import adapter from 'webrtc-adapter';
import BaseChat from './baseChat';
import BandwidthHandler from 'BandwidthHandler';

let defaultOptions = {
    container: '.chat-container',
    dataChannel: false,
    alertOnClose: false
};

class VideoChat extends BaseChat {

    /**
     * @type {MediaStream}
     */
    localStream = null;

    /**
     * @type {Array<MediaStreamTrack>}
     */
    localTracks = [];

    /**
     * @type {Array<RTCPeerConnection>}
     */
    connections = [];

    /**
     * @type {Array<MediaStream>}
     */
    videoDevices = [];

    /**
     * @type {Array<MediaStream>}
     */
    audioDevices = [];

    /**
     * @type {string}
     */
    currentVideoDeviceId = null;

    /**
     * @type {string}
     */
    currentAudioDeviceId = null;

    audioDeviceBandwidth = 64;

    videoDeviceBandwidth = 1024;

    constructor(url, options) {

        options = {
            ...defaultOptions,
            ...options
        }

        super(url, options);

        this.container = document.querySelector(this.options.container);

        this.socket.on('direct', (data) => this.onDirect(data));
    }

    async initVideoCall() {
        this.createLocalVideo();
        
        await this.loadDevices();
        
        await this.displayLocalStream(this.videoDevices[0], this.audioDevices[0]);
    }

    getId() {
        return new Promise( (resolve, reject) => {
            this.socket.on('connect', () => {
                resolve(this.socket.id);
            });
        });
    }

    onDirect(data) {
        var fromId = data.from.id;
        console.log('RTCNetwork: reveiced message from '+fromId+':', data);

        switch (data.type) {
            case 'offer':
                // EMIT 'offer'
                console.log('RTCNetwork: PeerConnection(' + fromId + '): offer received', data);
                this.acceptCall(fromId, data.offer);
                break;

            case 'answer':
                // EMIT 'answer'
                if (this.connections[fromId]) {
                    console.log('RTCNetwork: PeerConnection(' + fromId + '): answer received', data);
                    this.connections[fromId].setRemoteDescription(new RTCSessionDescription(data.answer));
                }
                break;

            case 'icecandidate':
                if (this.connections[fromId]) {
                    console.log('RTCNetwork: PeerConnection(' + fromId + '): candidate received', data);
                    // var candidate = new RTCIceCandidate({
                    //     sdpMLineIndex: data.sdpMLineIndex,
                    //     candidate: data.candidate
                    // });

                    this.connections[fromId].addIceCandidate(data.icecandidate);
                }
                break;

            default:
                console.log('received unrecognized message from ' + fromId + ': ' + data);

                this.socket.emit('direct', {
                    to: fromId,
                    type: 'unknown-message'
                });
        }
    }

    async makeCall(userID) {
        console.log('makeCall called to: ', userID);

        // const configuration = {'iceServers': [{'urls': 'stun:stun.l.google.com:19302'}]}
        // this.connections[userID] = new RTCPeerConnection(configuration);

        this.connections[userID] = this.createPeerConnection(userID);
        // console.log('make call: ', this.connections);

        if(this.options.dataChannel) {
            this.dataChannel = this.connections[userID].createDataChannel("data", { reliable: true });
            this.dataChannel.binaryType = 'arraybuffer';
    
            this.dataChannel.addEventListener('open', (event) => {
                console.log('Datachannel open: ', event);

                // this.createDataView(dataChannel);
            });
        }

        // TODO: verificare su firefox
        const offer = await this.connections[userID].createOffer({
            'mandatory': {
                'OfferToReceiveAudio': true,
                'OfferToReceiveVideo': true
            }
        });

        offer.sdp = BandwidthHandler.setApplicationSpecificBandwidth(offer.sdp, {
            audio: this.audioDeviceBandwidth,
            video: this.videoDeviceBandwidth
        }, false);

        await this.connections[userID].setLocalDescription(offer);

        // console.log('sending offer to ' + userID + ': ', offer);

        this.socket.emit('direct', {
            to: userID,
            type: 'offer',
            offer: offer
        })
    }

    closeCall(userID) {
        // console.log("CLOSING CALL", this.connections);

        const peerConnection = this.connections[userID];

        if(peerConnection) {
            peerConnection.removeStream(this.localStream);
            peerConnection.close();

            delete this.connections[userID];
        }

        const connectionsUsersId = Object.keys(this.connections);

        if (connectionsUsersId.length == 0) {
            this.localStream.getTracks().forEach((track) => {
                if (track.readyState == 'live') {
                    track.stop();
                }
            });
        }
    }

    closeAllCall() {
        // console.log("CLOSING CALL", this.connections);

        const connectionsUsersId = Object.keys(this.connections);

        if (connectionsUsersId.length > 0) {
            connectionsUsersId.forEach((userID) => {
                const peerConnection = this.connections[userID];

                if(peerConnection) {
                    peerConnection.removeStream(this.localStream);
                    peerConnection.close();

                    delete this.connections[userID];
                }
            });
        }

        this.localStream.getTracks().forEach((track) => {
            if (track.readyState == 'live') {
                track.stop();
            }
        });
    }

    createPeerConnection(userID) {
        // console.log('createPeerConnection called');

        const configuration = { 'iceServers': [{ 'urls': 'stun:stun.l.google.com:19302' }] }
        let connection = new RTCPeerConnection(configuration, {optional: [{'DtlsSrtpKeyAgreement': true}]});
        // console.log('iceGatheringState: ', connection.iceGatheringState)

        // Listen for local ICE candidates on the local RTCPeerConnection
        // connection.addEventListener('icecandidate', event => {
        //     console.log('icecandidate: ', event);

        //     if (event.candidate) {
        //         console.log('ICE candidate received from ICE server, signaling to peer (' + event.candidate + ')');

        //         this.socket.emit('direct', {
        //             to: userID,
        //             type: 'icecandidate',
        //             icecandidate: event.candidate,
        //         });
        //     }
        // });

        if (this.localStream) {
            this.localStream.getTracks().forEach((track) => {
                console.log('sending track: ' + track);
                const localRtpSender = connection.addTrack(track, this.localStream);
                // TODO: localTracks viene utilizzato solo qui ... credo non sia utile
                this.localTracks.push(localRtpSender);
            });
        } else {
            console.warn('No stream to send!');
        }

        let remoteStream = new MediaStream();
        connection.addEventListener('track', async (event) => {
            console.log('received track: ', event.track);

            remoteStream.addTrack(event.track);
        });


        connection.addEventListener('icecandidate', (event) => {
            // console.log('icecandidate: ', event);

            if (event.candidate) {
                // console.log('ICE candidate received from ICE server, signaling to peer (' + event.candidate + ')');

                this.socket.emit('direct', {
                    to: userID,
                    type: 'icecandidate',
                    icecandidate: event.candidate,
                });
            }
        });

        let remoteContainer;

        // Listen for connectionstatechange on the local RTCPeerConnection
        connection.addEventListener('connectionstatechange', (event) => {
            console.log('event connectionstatechange: ', event);

            // todo: verificare se sia il caso di prelevare l'oggetto connection da event
            // if (event.connectionState === connection.connectionState) {
            //     console.log('uguali');
            // } else {
            //     console.log('connection.connectionState: ', connection.connectionState);
            //     console.log('event.connectionState: ', event.connectionState);
            // }
            // if (event.connection === connection) {
            //     console.log("uguali");
            // } else {
            //     console.log('connection: ', connection);
            //     console.log('event.connection: ', event.connection);
            // }

            switch (connection.connectionState.toLowerCase()) {
                case 'connected':
                    console.log('Peer ' + userID + ' connected!');
                    remoteContainer = this.createRemoteVideo(remoteStream);
                    break;
                case 'failed':
                    console.log('Peer ' + userID + ' connection failed!');
                    remoteContainer.remove();
                    break;
                case 'disconnected':
                case 'closed':
                    console.log('Peer ' + userID + ' connection ' + connection.connectionState);
                    remoteContainer.remove();
                    this.closeCall(userID);

                    if(this.options.alertOnClose) {
                        alert("La chiamata è stata terminata.");
                    }
                    break;
            }
        });

        return connection;
    }

    async acceptCall(userID, offer) {
        // console.log('acceptCall called');

        this.connections[userID] = this.createPeerConnection(userID);
        this.connections[userID].setRemoteDescription(new RTCSessionDescription(offer));

        if(this.options.dataChannel) {
            this.connections[userID].addEventListener('datachannel', event => {
                const dataChannel = event.channel;
    
                dataChannel.addEventListener('open', (event) => {
                    console.log('Datachannel open: ', event);
    
                    // for (let i=0; i<5; i++) {
                    //     setTimeout(() => {
                    //         dataChannel.send(i);
                    //     }, (i+1) * 1000);
                    // }
                });
        
                dataChannel.addEventListener('close', (event) => {
                    console.log('Datachannel closed: ', event);
                });
        
                dataChannel.addEventListener('message', (event) => {
                    console.log('Datachannel message: ', event);
                });
        
                dataChannel.addEventListener('error', (event) => {
                    console.error('Datachannel error: ', event);
                });
            });
        }

        const answer = await this.connections[userID].createAnswer({
            'mandatory': {
                'OfferToReceiveAudio': true,
                'OfferToReceiveVideo': true
            }
        });

        answer.sdp = BandwidthHandler.setApplicationSpecificBandwidth(answer.sdp, {
            audio: this.audioDeviceBandwidth,
            video: this.videoDeviceBandwidth
        }, false);

        this.connections[userID].setLocalDescription(answer);

        // console.log('sending answer to ' + userID + ': ', answer);

        this.socket.emit('direct', {
            to: userID,
            type: 'answer',
            answer: answer
        });
    }

    // createControls() {
    //     let controlsContainer = document.createElement('div');
    //     controlsContainer.className = 'controls-container';

    //     let remoteIdInput = document.createElement('input');
    //     remoteIdInput.className = 'remote-id-input';
    //     remoteIdInput.type = 'text';
    //     controlsContainer.appendChild(remoteIdInput);

    //     let submitCall = document.createElement('button');
    //     submitCall.id = 'call-submit';
    //     submitCall.innerHTML = 'Call';
    //     submitCall.addEventListener('click', () => {
    //         if (remoteIdInput.value) {
    //             this.makeCall(remoteIdInput.value);
    //         }
    //     });
    //     controlsContainer.appendChild(submitCall);

    //     this.container.appendChild(controlsContainer);
    // }

    createLocalVideo() {
        let localVideoContainer = document.createElement('div');
        localVideoContainer.className = 'video-container video-container-local';

        // let localTitle = document.createElement('h3');
        // localTitle.innerHTML = 'Local video';
        // localVideoContainer.appendChild(localTitle);

        this.localVideoEl = document.createElement('video');
        this.localVideoEl.className = 'video video-local';
        this.localVideoEl.autoplay = true;
        this.localVideoEl.playsInline = true;
        this.localVideoEl.muted = true;
        this.localVideoEl.controls = false;
        // this.localVideoEl.addEventListener('loadedmetadata', () => {
        //     this.localVideoEl.play();
        // });
        localVideoContainer.appendChild(this.localVideoEl);

        // let localControls = document.createElement('div');
        // localControls.className = 'local-controls';

        let localVideoControls = document.createElement('div');
        localVideoControls.className = 'video-controls video-controls-local';

        // this.cameraSelectEl = document.createElement('select');
        // this.cameraSelectEl.id = 'camera-select';
        // this.cameraSelectEl.disabled = true;
        // this.cameraSelectEl.addEventListener('change', () => {
        //     console.log('video selection change: ', this.cameraSelectEl.value);

        //     this.displayLocalStream(this.cameraSelectEl.value);
        // });
        // localVideoControls.appendChild(this.cameraSelectEl);

        this.localVideoPausedEl = document.createElement('input');
        this.localVideoPausedEl.id = 'video-local-pause';
        this.localVideoPausedEl.type = 'checkbox';
        this.localVideoPausedEl.addEventListener('change', () => {
            this.pauseLocalVideo(this.localVideoPausedEl.checked);
        });

        localVideoControls.style.display = 'none';

        localVideoControls.appendChild(this.localVideoPausedEl);

        this.localVideoPausedLabel = document.createElement('label');
        this.localVideoPausedLabel.htmlFor = 'video-local-pause';
        this.localVideoPausedLabel.innerHTML = 'pause';
        localVideoControls.appendChild(this.localVideoPausedLabel);

        localVideoContainer.appendChild(localVideoControls);


        let localAudioControls = document.createElement('div');
        localAudioControls.className = 'audio-local-controls';

        // this.micSelectEl = document.createElement('select');
        // this.micSelectEl.id = 'mic-select';
        // this.micSelectEl.disabled = true;
        // this.micSelectEl.addEventListener('change', () => {
        //     console.log('audio selection change: ', this.micSelectEl.value);

        //     this.displayLocalStream(null, this.micSelectEl.value);
        // });
        // localAudioControls.appendChild(this.micSelectEl);

        this.localMicMutedEl = document.createElement('input');
        this.localMicMutedEl.id = 'local-mic-mute';
        this.localMicMutedEl.type = 'checkbox';
        this.localMicMutedEl.innerHTML = 'mute';
        this.localMicMutedEl.addEventListener('change', () => {
            this.muteLocalAudio(this.localMicMutedEl.checked);
        });

        localAudioControls.style.display = 'none';

        localAudioControls.appendChild(this.localMicMutedEl);

        let localMicMutedLabel = document.createElement('label');
        localMicMutedLabel.htmlFor = 'local-mic-mute';
        localMicMutedLabel.innerHTML = 'mute';

        localAudioControls.appendChild(localMicMutedLabel);

        localVideoContainer.appendChild(localAudioControls);

        this.container.appendChild(localVideoContainer);
    }

    createRemoteVideo(remoteStream) {
        let remoteVideoContainer = document.createElement('div');
        remoteVideoContainer.className = 'video-container video-container-remote';

        // let remoteTitle = document.createElement('h3');
        // remoteTitle.innerHTML = 'Remote video';
        // remoteVideoContainer.appendChild(remoteTitle);
        
        let remoteVideoEl = document.createElement('video');
        remoteVideoEl.className = 'video video-remote';
        remoteVideoEl.autoplay = true;
        remoteVideoEl.playsInline = true;
        remoteVideoEl.controls = false;
        // remoteVideoEl.addEventListener('loadedmetadata', () => {
        //     remoteVideoEl.play();
        // });

        remoteVideoEl.srcObject = remoteStream;

        remoteVideoContainer.appendChild(remoteVideoEl);


        this.container.appendChild(remoteVideoContainer);

        return remoteVideoContainer;
    }

    // createDataView(dataChannel) {
    //     let dataView = document.createElement('div');
    //     dataView.className = 'data-view';

    //     this.container.appendChild(dataView);

    //     dataChannel.addEventListener('close', (event) => {
    //         console.log('Datachannel closed: ', event);
    //         dataView.remove();
    //     });

    //     dataChannel.addEventListener('message', (event) => {
    //         console.log('Datachannel message: ', event);

    //         dataView.innerHTML += event.data + ' ';
    //     });

    //     dataChannel.addEventListener('error', (event) => {
    //         console.error('Datachannel error: ', event);
    //         dataView.remove();
    //     });
    // }

    async loadDevices() {
        if(!navigator.mediaDevices) {
            throw new Error('Nessun dispositivo utilizzabile. E\' necessario Verificare la configurazione del browser prima di utilizzare la funzionalità di videochiamata.');
        }

        if (navigator.mediaDevices.getUserMedia) {
            try {
                // TODO: capire a cosa serve il seguente codice
                let stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        mandatory: {
                            maxWidth: 720,
                            // maxHeight: 480
                        },
                        // optional: []
                    },
                    audio: true
                });

                // TODO: capire a cosa serve il seguente codice
                stream.getTracks().forEach((track) => {
                    track.stop();
                });
            }
            catch (err) {
                console.error(err.name + ': ' + err.message);
                throw new Error('Per utilizzare la chat è necessario permettere l\'accesso alla videocamera e al microfono.');
            }

            try {
                let devices = await navigator.mediaDevices.enumerateDevices();

                devices.forEach( (device) => {
                    console.log(device);

                    switch(device.kind) {
                        case 'videoinput':
                            this.videoDevices.push(device);
                            break;
                        case 'audioinput':
                            this.audioDevices.push(device);
                            break;
                        default:
                            // console.log('VideoChat::loadDevices: Not interested in device of type ' + device.kind + '. Ignoring...', device);
                            break;
                    }
                });

                return devices;
            } catch (err) {
                console.error(err.name + ': ' + err.message);
                throw new Error('Impossibile accedere alle risorse multimediali.');
            }
        }
    }

    async displayLocalStream(videoDeviceId = null, audioDeviceId = null) {
        // if (!videoDeviceId && !audioDeviceId) {
        //     return;
        // }

        if (!videoDeviceId) {
            videoDeviceId = this.currentVideoDeviceId ? this.currentVideoDeviceId : this.videoDevices[0].deviceId;
        }

        if (!audioDeviceId) {
            audioDeviceId = this.currentAudioDeviceId ? this.currentAudioDeviceId : this.audioDevices[0].deviceId;
        }

        try {
            let stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    mandatory: {
                        maxWidth: 640,
                        maxHeight: 480
                    },
                    deviceId: videoDeviceId
                },
                audio: {
                    deviceId: audioDeviceId
                }
            });

            this.currentVideoDeviceId = videoDeviceId;
            this.currentAudioDeviceId = audioDeviceId;
            this.localStream = stream;

            console.log('VideoChat::displayLocalStream: now displaying ', stream);

            this.localVideoPausedEl.checked = false;
            this.localMicMutedEl.checked = false;
            this.localVideoEl.srcObject = stream;

            // DEBUG
            // this.muteLocalAudio(true);
            // END DEBUG

        } catch (err) {
            console.error('VideoChat::displayLocalStream: ' + err.name + ': ' + err.message);

            // TODO: perché durante l'eccezione vengono solvati questi valori?
            if(this.cameraSelectEl) {
                this.cameraSelectEl.value = this.currentVideoDeviceId;
            }

            if(this.micSelectEl) {
                this.micSelectEl.value = this.currentAudioDeviceId;
            }

            return;
        }
    }

    pauseLocalVideo(pause) {
        if (!this.localStream) {
            return;
        }

        this.localStream.getTracks().forEach((track) => {
            // console.log("kind: ", track.kind);

            if (track.kind == 'video') {
                track.enabled = !pause;
                console.log("pause local video: ", track, !pause);
                this.localVideoPausedEl.checked = pause;
            }
        });
    }

    muteLocalAudio(mute) {
        if (!this.localStream) {
            return;
        }

        this.localStream.getTracks().forEach((track) => {
            // console.log("kind: ", track.kind);

            if (track.kind == 'audio') {
                // console.log("mute local audio: ", track, !mute);
                track.enabled = !mute;
                this.localMicMutedEl.checked = mute;
            }
        });
    }
}

export default VideoChat;